import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { dateFields, type DateFields } from './libraryPresent';

/**
 * 素材庫日期顯示 — CIS v1.9 寫法(D3 08-07 清單 A 節「日期寫法 v1.9 落地」)。
 * -----------------------------------------------------------------------------
 * 設計稿主格是 `8月 3, 2026`(`library.js` `MON3`/`fmtD`),燈箱資訊卡是
 * `8月 3, 2026 14:22`(`dstamp`)。**不能逐字硬抄** —— 那串是 zh-TW 的寫法,
 * en / ja 各有自己的自然序,而 `hai/no-hardcoded-cjk` 也不讓字串留在元件裡。
 *
 * 作法 = **月份名走 `Intl`、組句走 i18n pattern**:
 *   - `Intl.DateTimeFormat(lang, {month:'short'})` → zh-TW `8月` / en `Aug` / ja `8月`
 *   - `library:libraryView.date.full` = `{{mon}} {{d}}, {{y}}`(zh-TW·en)
 *     / `{{y}}年{{mon}}{{d}}日`(ja)
 * 合起來 zh-TW 就是設計稿要的 `8月 3, 2026`,另外兩語也不會變成中文語序。
 *
 * ⚠️ `Intl` 的 locale 用 i18next 的 `resolvedLanguage`(不是 `language` —— 後者可能
 * 是還沒解析的 `zh` 之類)。取不到就退 `undefined` 讓 Intl 用系統預設,不要硬塞。
 */
export interface LibraryDateFormat {
  /** 主格:`8月 3, 2026`。解析失敗 → 回傳 raw 字串原樣顯示(不偽造日期)。 */
  fmtD: (iso: string) => string;
  /** 燈箱資訊卡:`8月 3, 2026 14:22`。 */
  dstamp: (iso: string) => string;
  /** 副標時分:`14:22`(解析失敗 → 空字串)。 */
  timeOf: (iso: string) => string;
  /** 已經拆好的欄位直接組主格(清單的檢查日期欄走 `checkedAtFields`)。 */
  fmtFields: (f: DateFields) => string;
}

export function useLibraryDateFormat(): LibraryDateFormat {
  const { t, i18n } = useTranslation('library');
  const lang = i18n.resolvedLanguage || undefined;

  const monthShort = useCallback(
    (m: number): string => {
      // 用固定年份 + 該月 1 號取月份名;day/year 不參與輸出
      const sample = new Date(2026, m - 1, 1);
      try {
        return new Intl.DateTimeFormat(lang, { month: 'short' }).format(sample);
      } catch {
        // 不合法 locale tag(理論上不會發生)→ 退數字月,不要整格空掉
        return String(m);
      }
    },
    [lang],
  );

  const fmtFields = useCallback(
    (f: DateFields): string =>
      t('libraryView.date.full', { mon: monthShort(f.m), d: f.d, y: f.y }),
    [t, monthShort],
  );

  const fmtD = useCallback(
    (iso: string): string => {
      const f = dateFields(iso);
      return f ? fmtFields(f) : iso;
    },
    [fmtFields],
  );

  const timeOf = useCallback((iso: string): string => {
    const f = dateFields(iso);
    return f ? `${f.hh}:${f.mm}` : '';
  }, []);

  const dstamp = useCallback(
    (iso: string): string => {
      const f = dateFields(iso);
      return f ? `${fmtFields(f)} ${f.hh}:${f.mm}` : iso;
    },
    [fmtFields],
  );

  return { fmtD, dstamp, timeOf, fmtFields };
}
